import { location, onCleanup, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { downArrow, tdesignCheck as checkIcon } from '../../../assets/commonIcons';
import { CurrencyFlag } from '../../../components/CurrencyFlag';
import filterIcon from '../../../assets/deposit-records/filter.svg';
import arrowRight from '../../../assets/deposit-records/arrow-right.svg';
import { showActionSheet } from '../components/ActionSheet';
import { DatePicker } from '../components/DatePicker';
import { showModal } from '../components/Modal';
import { bootFundRecordsApp } from '../deposit/h5-deposit/appBridge';
import { DiFiNoData } from '../../../components/DiFi/DiFiNoData';
import { runWhenIdle } from '../account/open-progress/idlePreload';
import { RecordShell } from './Shell';
import {
  canCancelKind,
  cancelConfirmText,
  cancelRecord,
  depositStatus,
  detailUrl,
  fetchRecordAccounts,
  fetchRecordPage,
  groupDeposits,
  isAllowCancel,
  rangeQuery,
  recordAccountLabel,
  recordKindOf,
  recordLines,
  recordRowId,
  recordTitle,
  type RangeKey,
  type RecordKind,
} from './shared';
import './deposit-records.css';

/** 空闲预拉顺序：每次 idle 只拉一个 kind 第 1 页 */
const KIND_WARM_ORDER: readonly RecordKind[] = ['deposit', 'withdraw', 'exchange', 'transfer'];
/** 弱机兜底：最迟约 1.2s 也推进下一 kind 预拉 */
const KIND_WARM_IDLE_TIMEOUT_MS = 1200;

type KindCacheEntry = {
  list: any[];
  page: number;
  hasMore: boolean;
  ready: boolean;
  failed: boolean;
};

/** 列表卡片骨架：对齐 deposit-records-card（金额行 + 两行 meta） */
const RecordCardSkeleton = () => (
  <div class="deposit-records-card deposit-records-card-skeleton difi-skeleton-animate" aria-busy="true">
    <div class="deposit-records-card-top">
      <div class="deposit-records-skel-line deposit-records-skel-amount" />
      <div class="deposit-records-skel-line deposit-records-skel-more" />
    </div>
    <div class="deposit-records-metas">
      <div class="deposit-records-meta">
        <div class="deposit-records-skel-line deposit-records-skel-meta-label" />
        <div class="deposit-records-skel-line deposit-records-skel-meta-value" />
      </div>
      <div class="deposit-records-meta">
        <div class="deposit-records-skel-line deposit-records-skel-meta-label" />
        <div class="deposit-records-skel-line deposit-records-skel-meta-value-short" />
      </div>
    </div>
  </div>
);

const RecordListSkeleton = () => (
  <div class="deposit-records-skel-wrap">
    <div class="deposit-records-group">
      <div class="deposit-records-skel-line deposit-records-skel-date" />
    </div>
    <RecordCardSkeleton />
    <RecordCardSkeleton />
  </div>
);

const chips = (): { key: RecordKind; label: string }[] => [
  { key: 'deposit', label: i18n.MoreService.Deposit },
  { key: 'withdraw', label: i18n.MoreService.Withdraw },
  { key: 'exchange', label: i18n.MoreService.Exchange },
  { key: 'transfer', label: i18n.FundRecords.TabTransfer },
];

const currencies = () => [
  { key: '', label: i18n.FundRecords.CurrencyAll },
  { key: 'HKD', label: i18n.FundH5.CurrencyHkd },
  { key: 'USD', label: i18n.FundH5.CurrencyUsd },
  { key: 'CNY', label: i18n.FundH5.CurrencyCny },
];

const ranges = (): { key: RangeKey; label: string }[] => [
  { key: 'month', label: i18n.FundRecords.Range1Month },
  { key: 'quarter', label: i18n.FundRecords.Range3Months },
  { key: 'year', label: i18n.FundRecords.Range1Year },
  { key: 'ytd', label: i18n.FundRecords.RangeYtd },
  { key: 'custom', label: i18n.FundRecords.RangeCustom },
];

/** routeReplace 后 cdui 的 location.query 可能滞后于 window.location.search；
 *  与 isFundBackToApp 一致，优先读真实 URL，再退回 location.query。 */
const readSearchParam = (key: string) => {
  try {
    return new URLSearchParams(window.location.search).get(key) || '';
  } catch {
    return '';
  }
};

const pickAccount = (accounts: any[]) => {
  const nums = accounts.map((row) => String(row?.accountNum ?? '').trim()).filter(Boolean);
  const q = (location.query || {}) as Record<string, string>;
  const fromUrl = readSearchParam('accountNum').trim();
  const fromQuery = String(q.accountNum || '').trim();
  console.log('[fund-records-list] pickAccount 内部', {
    windowSearch: window.location.search,
    windowHref: window.location.href,
    fromUrl,
    fromQuery,
    locationQuery: q,
    nums,
  });
  const want = fromUrl || fromQuery;
  if (want) {
    // URL 带的户号若在账户列表里直接用；不在也用，避免 URL 与选中态不一致
    if (nums.includes(want)) return want;
    return want;
  }

  const mode = (readSearchParam('mode').trim() || String(q.mode || '').trim()) as string;
  const modeType = (readSearchParam('modeType').trim() || String(q.modeType || '').trim()) as string;
  if (mode === 'virtual') {
    const crypto = accounts.find((row) => String(row?.productType ?? '').trim() === 'C');
    const num = crypto ? String(crypto.accountNum ?? '').trim() : '';
    if (num) return num;
  }
  if (mode === 'security' && (modeType === 'C' || modeType === 'M')) {
    const hit = accounts.find(
      (row) =>
        String(row?.productType ?? '').trim() !== 'C' && String(row?.type ?? '').trim() === modeType,
    );
    const num = hit ? String(hit.accountNum ?? '').trim() : '';
    if (num) return num;
  }

  return nums[0] || '';
};

export const DepositRecordList = () => {
  const state = reactive({
    accounts: [] as any[],
    accountNum: '',
    kind: recordKindOf(location.query.type) as RecordKind,
    currency: '',
    range: 'month' as RangeKey,
    from: '',
    to: '',
    draftCurrency: '',
    draftRange: 'month' as RangeKey,
    draftFrom: '',
    draftTo: '',
    list: [] as any[],
    page: 1,
    hasMore: false,
    loading: true,
    loadingMore: false,
    ready: false,
    failed: false,
    cancelling: '',
  });

  let loadSeq = 0;
  let warmSeq = 0;
  let idleToken = 0;
  let disposed = false;
  let pullStart = 0;
  /** 当前 cache 绑定的筛选键；变更时整表失效 */
  let filterKey = '';
  const cache: Partial<Record<RecordKind, KindCacheEntry>> = {};

  const makeFilterKey = () =>
    `${state.accountNum}|${state.currency}|${state.range}|${state.from}|${state.to}`;

  const invalidateCache = () => {
    for (const kind of KIND_WARM_ORDER) delete cache[kind];
    filterKey = '';
    warmSeq += 1;
    idleToken += 1;
  };

  const putCache = (kind: RecordKind, entry: KindCacheEntry) => {
    const key = makeFilterKey();
    if (filterKey && filterKey !== key) {
      for (const k of KIND_WARM_ORDER) delete cache[k];
    }
    filterKey = key;
    cache[kind] = {
      list: entry.list.slice(),
      page: entry.page,
      hasMore: entry.hasMore,
      ready: entry.ready,
      failed: entry.failed,
    };
  };

  const getCache = (kind: RecordKind): KindCacheEntry | null => {
    if (!filterKey || makeFilterKey() !== filterKey) return null;
    return cache[kind] ?? null;
  };

  const applyCacheToState = (entry: KindCacheEntry) => {
    state.list = entry.list.slice();
    state.page = entry.page;
    state.hasMore = entry.hasMore;
    state.ready = entry.ready;
    state.failed = entry.failed;
    state.loading = false;
    state.loadingMore = false;
  };

  const snapshotCurrentToCache = () => {
    if (!state.accountNum || !state.ready) return;
    putCache(state.kind, {
      list: state.list,
      page: state.page,
      hasMore: state.hasMore,
      ready: state.ready,
      failed: state.failed,
    });
  };

  const accountText = () => {
    const hit = state.accounts.find((row) => String(row?.accountNum ?? '') === state.accountNum);
    if (hit) return recordAccountLabel(hit);
    if (state.accountNum) return state.accountNum;
    return i18n.FundRecords.PhSelectAccount;
  };

  const scheduleWarmNext = () => {
    if (disposed || !state.accountNum) return;
    const next = KIND_WARM_ORDER.find((kind) => kind !== state.kind && !getCache(kind)?.ready);
    if (!next) return;
    const token = idleToken;
    runWhenIdle(() => {
      if (disposed || token !== idleToken) return;
      void warmKind(next);
    }, KIND_WARM_IDLE_TIMEOUT_MS);
  };

  const warmKind = async (kind: RecordKind) => {
    if (disposed || !state.accountNum) return;
    if (kind === state.kind) {
      scheduleWarmNext();
      return;
    }
    if (getCache(kind)?.ready) {
      scheduleWarmNext();
      return;
    }
    const seq = ++warmSeq;
    const keyAtStart = makeFilterKey();
    const query = rangeQuery(state.range, state.from, state.to);
    try {
      const rows = await fetchRecordPage(kind, {
        accountNum: state.accountNum,
        from: query.from,
        to: query.to,
        page: 1,
        currency: state.currency,
      });
      if (disposed || seq !== warmSeq) return;
      if (makeFilterKey() !== keyAtStart) return;
      putCache(kind, {
        list: rows,
        page: 1,
        hasMore: rows.length >= 25,
        ready: true,
        failed: false,
      });
    } catch {
      if (disposed || seq !== warmSeq) return;
      if (makeFilterKey() !== keyAtStart) return;
      putCache(kind, {
        list: [],
        page: 1,
        hasMore: false,
        ready: true,
        failed: true,
      });
    }
    scheduleWarmNext();
  };

  const load = async (page: number, replace: boolean) => {
    if (!state.accountNum) return;
    if (!replace && (state.loadingMore || state.loading || !state.hasMore)) return;
    const seq = ++loadSeq;
    const kindAtStart = state.kind;
    const keyAtStart = makeFilterKey();
    if (replace) {
      state.loading = state.list.length === 0;
      state.loadingMore = false;
    } else {
      state.loadingMore = true;
    }
    const query = rangeQuery(state.range, state.from, state.to);
    try {
      console.log('[fund-records-list] ⑤ fetchRecordPage 请求', {
        kind: kindAtStart,
        accountNum: state.accountNum,
        from: query.from,
        to: query.to,
        page,
        currency: state.currency,
      });
      const rows = await fetchRecordPage(kindAtStart, {
        accountNum: state.accountNum,
        from: query.from,
        to: query.to,
        page,
        currency: state.currency,
      });
      console.log('[fund-records-list] ⑤ fetchRecordPage 完成', { count: rows.length });
      if (disposed || seq !== loadSeq) return;
      if (state.kind !== kindAtStart || makeFilterKey() !== keyAtStart) return;
      state.list = replace ? rows : state.list.concat(rows);
      state.page = page;
      state.hasMore = rows.length >= 25;
      state.failed = false;
      state.ready = true;
      putCache(kindAtStart, {
        list: state.list,
        page: state.page,
        hasMore: state.hasMore,
        ready: true,
        failed: false,
      });
      if (replace) scheduleWarmNext();
    } catch (e) {
      console.log('[fund-records-list] ⑤ fetchRecordPage 失败', e);
      if (disposed || seq !== loadSeq) return;
      if (state.kind !== kindAtStart || makeFilterKey() !== keyAtStart) return;
      if (replace && state.list.length === 0) {
        state.failed = true;
        state.hasMore = false;
      }
      state.ready = true;
      putCache(kindAtStart, {
        list: state.list,
        page: state.page,
        hasMore: state.hasMore,
        ready: true,
        failed: state.failed,
      });
      if (replace) scheduleWarmNext();
    } finally {
      if (seq === loadSeq) {
        state.loading = false;
        state.loadingMore = false;
      }
    }
  };

  /** 清空当前展示并重拉；invalidateAll 用于换账户 / 改筛选 */
  const reload = (opts?: { invalidateAll?: boolean }) => {
    if (opts?.invalidateAll) invalidateCache();
    else delete cache[state.kind];
    state.list = [];
    state.page = 1;
    state.hasMore = false;
    state.ready = false;
    state.failed = false;
    state.loading = Boolean(state.accountNum);
    if (state.accountNum) void load(1, true);
  };

  const selectKind = (kind: RecordKind) => {
    if (state.kind === kind) return;
    snapshotCurrentToCache();
    state.kind = kind;
    const hit = getCache(kind);
    if (hit?.ready) {
      applyCacheToState(hit);
      scheduleWarmNext();
      return;
    }
    state.list = [];
    state.page = 1;
    state.hasMore = false;
    state.ready = false;
    state.failed = false;
    state.loading = Boolean(state.accountNum);
    if (state.accountNum) void load(1, true);
  };

  onMount(() => {
    void (async () => {
      console.log('[fund-records-list] ① bootFundRecordsApp 开始');
      try {
        await bootFundRecordsApp();
        console.log('[fund-records-list] ① bootFundRecordsApp 完成');
      } catch (e) {
        console.log('[fund-records-list] ① bootFundRecordsApp 失败', e);
        state.loading = false;
        state.ready = true;
        return;
      }
      try {
        console.log('[fund-records-list] ② fetchRecordAccounts 开始');
        const accounts = await fetchRecordAccounts();
        console.log('[fund-records-list] ② fetchRecordAccounts 完成', { count: accounts.length });
        state.accounts = accounts;
        state.accountNum = pickAccount(accounts);
        console.log('[fund-records-list] ③ pickAccount', { accountNum: state.accountNum });
      } catch (e) {
        console.log('[fund-records-list] ② fetchRecordAccounts 失败', e);
        state.accounts = [];
      }
      const preview = rangeQuery('month');
      state.from = preview.from;
      state.to = preview.to;
      if (state.accountNum) {
        console.log('[fund-records-list] ④ load(1, true) 开始');
        void load(1, true);
      } else {
        console.log('[fund-records-list] ④ 无 accountNum，显示空态');
        state.loading = false;
        state.ready = true;
      }
    })();
  });

  onCleanup(() => {
    disposed = true;
    loadSeq += 1;
    warmSeq += 1;
    idleToken += 1;
  });

  const openAccounts = () => {
    if (!state.accounts.length) return;
    const sheet = showActionSheet({
      title: i18n.FundH5.SelectAccount,
      contentClass: 'deposit-records-accounts',
      content: () => (
        <div>
          {state.accounts.map((row) => {
            const num = String(row?.accountNum ?? '');
            const active = num === state.accountNum;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  sheet.close();
                  if (num === state.accountNum) return;
                  state.accountNum = num;
                  reload({ invalidateAll: true });
                }}
              >
                <div class="action-sheet-option-label">{recordAccountLabel(row)}</div>
                {active ? (
                  <span
                    class="deposit-records-check"
                    style={{
                      'mask-image': `url("${checkIcon}")`,
                      '-webkit-mask-image': `url("${checkIcon}")`,
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const fillDraft = (key: RangeKey) => {
    state.draftRange = key;
    if (key === 'custom') return;
    const preview = rangeQuery(key);
    state.draftFrom = preview.from;
    state.draftTo = preview.to;
  };

  const openFilter = () => {
    state.draftCurrency = state.currency;
    state.draftRange = state.range;
    if (state.range === 'custom') {
      state.draftFrom = state.from;
      state.draftTo = state.to;
    } else {
      const preview = rangeQuery(state.range);
      state.draftFrom = preview.from;
      state.draftTo = preview.to;
    }
    const sheet = showActionSheet({
      title: i18n.FundRecords.Filter,
      contentClass: 'deposit-records-sheet',
      content: () => (
        <div>
          <div class="deposit-records-sheet-label">{i18n.FundRecords.TradeCurrency}</div>
          <div class="deposit-records-currencies">
            {currencies().map((item) => (
              <button
                type="button"
                class={`deposit-records-choice${state.draftCurrency === item.key ? ' deposit-records-choice-active' : ''}`}
                onclick={() => {
                  state.draftCurrency = item.key;
                }}
              >
                {item.key ? <CurrencyFlag code={item.key} /> : null}
                {item.label}
              </button>
            ))}
          </div>
          <div class="deposit-records-sheet-label">{i18n.FundRecords.TradeTime}</div>
          <div class="deposit-records-ranges">
            {ranges()
              .filter((item) => item.key !== 'custom')
              .map((item) => (
                <button
                  type="button"
                  class={`deposit-records-choice${state.draftRange === item.key ? ' deposit-records-choice-active' : ''}`}
                  onclick={() => fillDraft(item.key)}
                >
                  {item.label}
                </button>
              ))}
          </div>

          <div class="deposit-records-sheet-label">{i18n.FundRecords.CustomTime}</div>
          <div class="deposit-records-dates">
            <div
              class={`deposit-records-date${state.draftRange === 'custom' && state.draftFrom ? ' deposit-records-date-on' : ''}`}
            >
              <DatePicker
                title={i18n.FundRecords.StartDate}
                placeholder={i18n.FundRecords.StartDate}
                value={state.draftFrom}
                change={(value) => {
                  state.draftRange = 'custom';
                  state.draftFrom = value;
                }}
              />
            </div>
            <span class="deposit-records-date-dash">—</span>
            <div
              class={`deposit-records-date${state.draftRange === 'custom' && state.draftTo ? ' deposit-records-date-on' : ''}`}
            >
              <DatePicker
                title={i18n.FundRecords.EndDate}
                placeholder={i18n.FundRecords.EndDate}
                value={state.draftTo}
                change={(value) => {
                  state.draftRange = 'custom';
                  state.draftTo = value;
                }}
              />
            </div>
          </div>
          <div class="deposit-records-sheet-actions">
            <button
              type="button"
              class="deposit-records-sheet-reset"
              onclick={() => {
                state.draftCurrency = '';
                fillDraft('month');
              }}
            >
              {i18n.FundRecords.Reset}
            </button>
            <button
              type="button"
              class="deposit-records-sheet-ok"
              disabled={state.draftRange === 'custom' && (!state.draftFrom || !state.draftTo)}
              onclick={() => {
                let from = state.draftFrom;
                let to = state.draftTo;
                if (state.draftRange === 'custom' && from && to && from > to) {
                  const swap = from;
                  from = to;
                  to = swap;
                }
                state.currency = state.draftCurrency;
                state.range = state.draftRange;
                state.from = from;
                state.to = to;
                sheet.close();
                reload({ invalidateAll: true });
              }}
            >
              {i18n.FundH5.Confirm}
            </button>
          </div>
        </div>
      ),
    });
  };

  const askCancel = (row: any) => {
    if (!canCancelKind(state.kind)) return;
    const id = recordRowId(state.kind, row);
    if (!id || state.cancelling) return;
    showModal({
      title: i18n.FundRecords.CancelApplyTitle,
      content: () => cancelConfirmText(state.kind),
      cancelText: i18n.FundH5.No,
      confirmText: i18n.FundH5.Yes,
      onConfirm: () => {
        if (state.cancelling) return;
        state.cancelling = id;
        void cancelRecord(state.kind, id)
          .then(() => reload())
          .catch(() => undefined)
          .finally(() => {
            state.cancelling = '';
          });
      },
    });
  };

  const onScroll = (event: Event) => {
    const el = event.currentTarget as HTMLElement;
    const remain = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remain <= el.clientHeight * 0.2) void load(state.page + 1, false);
  };

  const onTouchStart = (event: TouchEvent) => {
    const el = event.currentTarget as HTMLElement;
    pullStart = el.scrollTop <= 0 ? event.touches[0]?.clientY || 0 : 0;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (!pullStart) return;
    const delta = (event.changedTouches[0]?.clientY || 0) - pullStart;
    pullStart = 0;
    if (delta > 64) void load(1, true);
  };

  const showEmpty = () => state.ready && !state.loading && state.list.length === 0;

  return (
    <RecordShell>
      <div class="deposit-records-account-bar">
        <button type="button" class="deposit-records-account" onclick={openAccounts}>
          <span>{accountText()}</span>
          <span
            class="deposit-records-account-arrow"
            style={{
              'mask-image': `url("${downArrow}")`,
              '-webkit-mask-image': `url("${downArrow}")`,
            }}
          />
        </button>
        <button type="button" class="deposit-records-filter" onclick={openFilter}>
          <span
            class="deposit-records-filter-icon"
            style={{
              'mask-image': `url("${filterIcon}")`,
              '-webkit-mask-image': `url("${filterIcon}")`,
            }}
          />
        </button>
      </div>
      <div class="deposit-records-chips">
        {chips().map((chip) => (
          <button
            type="button"
            class={`deposit-records-chip${state.kind === chip.key ? ' deposit-records-chip-active' : ''}`}
            onclick={() => selectKind(chip.key)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div
        class={`deposit-records-scroll${showEmpty() ? ' deposit-records-scroll-is-empty' : ''}`}
        onscroll={onScroll}
        ontouchstart={onTouchStart}
        ontouchend={onTouchEnd}
      >
        {state.loading && state.list.length === 0 ? <RecordListSkeleton /> : null}
        {showEmpty() ? (
          <div class="deposit-records-empty">
            <div style={{ 'margin-top': '-48px' }}>
              <DiFiNoData hasIcon text={i18n.FundRecords.Empty} />
            </div>
          </div>
        ) : null}
        {groupDeposits(state.list).map((group) => (
          <div>
            <div class="deposit-records-group">
              <div class="deposit-records-group-date">{group.date}</div>
            </div>
            {group.rows.map((row) => {
              const status = depositStatus(row?.status);
              const id = recordRowId(state.kind, row);
              const lines = recordLines(state.kind, row);
              const goDetail = () => {
                if (id) location.routeTo(detailUrl(state.kind, id, { allowCancel: isAllowCancel(row) }));
              };
              return (
                <div class="deposit-records-card" onclick={goDetail}>
                  <div class="deposit-records-card-top">
                    <div class="deposit-records-amount">{recordTitle(state.kind, row)}</div>
                    <button type="button" class="deposit-records-more" onclick={goDetail}>
                      {i18n.FundRecords.MoreActions}
                      <img src={arrowRight} alt="" />
                    </button>
                  </div>
                  <div class="deposit-records-metas">
                    {lines.map((line) => (
                      <div class="deposit-records-meta">
                        <span class="deposit-records-meta-label">{line.label}</span>
                        {line.status && status.tone !== 'muted' ? (
                          <span class={`deposit-records-status-tag deposit-records-status-tag-${status.tone}`}>
                            {status.text}
                          </span>
                        ) : (
                          <span
                            class={`deposit-records-meta-value${line.status ? ` deposit-records-status-${status.tone}` : ''}`}
                          >
                            {line.status ? status.text : line.value}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {isAllowCancel(row) && canCancelKind(state.kind) ? (
                    <button
                      type="button"
                      class="deposit-records-row-cancel"
                      disabled={state.cancelling === id}
                      onclick={(event: MouseEvent) => {
                        event.stopPropagation();
                        askCancel(row);
                      }}
                    >
                      {i18n.FundRecords.Revoke}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
        {state.loadingMore ? <div class="deposit-records-loading" /> : null}
        {state.ready && state.list.length > 0 && !state.hasMore ? (
          <div class="deposit-records-end">{i18n.FundRecords.LoadedAll}</div>
        ) : null}
      </div>
    </RecordShell>
  );
};
